<template>
  <div class="flex">
    <!-- Thumbnail controls -->
    <div
      class="pointer-events-none relative top-0 z-10 flex h-full w-1/5 flex-col justify-between pl-2 pr-4"
      :style="imageSizeTrackStyle"
    >
      <button
        type="button"
        class="thumbnail-ctrl-btn group pointer-events-auto bg-gradient-to-t from-white/0 via-white/100 via-40% to-white/100 to-100%"
        :class="{ invisible: !allowScrollUp }"
        :aria-label="$t('navigation.pagination.previous')"
        @click="scrollThumbs('up')"
      >
        <IconArrowUp class="thumbnail-ctrl-icon" />
      </button>
      <button
        type="button"
        class="thumbnail-ctrl-btn group pointer-events-auto relative bg-gradient-to-t from-white/100 via-white/75 via-75% to-white/0"
        :class="{ invisible: !allowScrollDown }"
        :aria-label="$t('navigation.pagination.next')"
        @click="scrollThumbs('down')"
      >
        <IconArrowTrueDown class="thumbnail-ctrl-icon" />
      </button>
    </div>

    <!-- Thumbnails wrapper with clipping -->
    <div class="ml-[-20%] flex w-1/6 lg:w-1/5" :style="imageSizeTrackStyle">
      <div class="relative size-full overflow-hidden p-[2px] pr-4">
        <!-- Thumbnails column -->
        <div
          ref="thumbsContainer"
          class="flex flex-col space-y-2 transition-transform duration-200"
          :style="thumbsOffsetStyle"
        >
          <div
            v-for="(item, index) in items"
            :key="index"
            class="cursor-pointer"
          >
            <button
              v-if="item && !('videoUrl' in item)"
              type="button"
              class="block w-full"
              :aria-label="`${productName} thumbnail ${index + 1}`"
              :aria-current="activeIndex === index ? 'true' : undefined"
              @click="setActiveImage(index)"
            >
              <NuxtLazyImage
                :source="item.hash"
                provider="default"
                sizes="120px"
                :alt="`${productName} thumbnail ${index + 1}`"
                fit="cover"
                class="w-full"
                :class="{ 'ring-2 ring-black-40': activeIndex === index }"
              />
            </button>
            <button
              v-else
              type="button"
              class="relative aspect-[7/10] bg-gold"
              :class="{
                'border-2 border-black-40 p-0.5': activeIndex === index,
              }"
              :aria-label="`${productName} video thumbnail`"
              :aria-current="activeIndex === index ? 'true' : undefined"
              @click="setActiveImage(index)"
            >
              <img
                v-if="placeholderSrc"
                :src="placeholderSrc"
                :alt="`${productName} video thumbnail`"
                class="size-full object-cover"
              />

              <span
                class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold px-4 py-2 font-bold text-white shadow-lg transition-all duration-75 hover:brightness-95 focus:ring-2 active:scale-95"
                aria-hidden="true"
              >
                <svg
                  class="size-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 5v14l11-7z"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main image column -->
    <div class="relative mx-auto w-full lg:h-full lg:w-4/5">
      <ViewportPriority on>
        <div
          ref="imageContainer"
          class="relative aspect-[7/10] overflow-hidden bg-gold-10"
        >
          <NuxtLazyImage
            v-if="activeItem && !('videoUrl' in activeItem)"
            :source="activeItem?.hash"
            :alt="productName"
            provider="default"
            :no-scale="true"
            class="w-full"
            :class="{ 'opacity-0': !isLoaded }"
            @mousemove="handleMouseMove"
            @mouseenter="startHovering"
            @mouseleave="stopHovering"
            @load="isLoaded = true"
          />
          <UiVideo
            v-else-if="activeItem && 'videoUrl' in activeItem"
            :src="activeItem.videoUrl"
            :placeholder-src="placeholderSrc"
            autoplay
            class="controls-hide-download w-full"
            aspect-ratio="7/10"
            loop
          />
          <!-- Magnifying lens -->
          <div
            v-if="activeItem && !('videoUrl' in activeItem)"
            class="pointer-events-none absolute overflow-hidden rounded-full border-2 border-white shadow"
            :style="magnifyingLensStyle"
            :class="{ hidden: !isHovering }"
          >
            <nuxt-img
              :src="activeItem.hash"
              :alt="productName"
              :style="zoomedImageStyle"
              class="absolute left-0 top-0 max-w-none"
              :class="{ hidden: !isHovering }"
            />
          </div>

          <!-- Mobile navigation arrows -->
          <div
            v-if="isMobile"
            class="absolute inset-x-0 top-1/2 flex items-center justify-between"
          >
            <button
              type="button"
              class="rounded-full bg-white/50 p-3 shadow-md"
              :aria-label="$t('pdp.gallery.previous_image')"
              @click="navigateImage('prev')"
            >
              <IconArrowLeft class="size-4 text-black" />
            </button>
            <button
              type="button"
              class="rounded-full bg-white/50 p-3 shadow-md"
              :aria-label="$t('pdp.gallery.next_image')"
              @click="navigateImage('next')"
            >
              <IconArrowRight class="size-4 text-black" />
            </button>
          </div>
        </div>
      </ViewportPriority>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, watch } from 'vue'
import type { ProductImage } from '@scayle/storefront-nuxt'
import type { SbProductConfig } from '~/cms/storyblok/types/storyblok.d.ts'
import useThumbnailControls from '#layers/base/composables/useThumbnailControls'

const props = defineProps({
  images: {
    type: Array as PropType<Array<ProductImage>>,
    required: true,
  },
  productName: {
    type: String,
    required: true,
  },
  productOrderNumber: {
    type: String,
    required: true,
  },
})

defineEmits(['click:image'])
const config = useRuntimeConfig()
const { isMobile } = useDeviceType()

const sortedImages = computed(() =>
  sortImagesByAssetType(filterOutRedundantImages(props.images, false), false),
)

// IMPORTANT: useThumbnailControls must be called BEFORE any await
// to avoid SSR memory leaks (it registers onMounted/onUnmounted/watch)
const imageContainer = ref<HTMLElement | null>(null)
const thumbsContainer = ref<HTMLElement | null>(null)

const {
  allowScrollUp,
  allowScrollDown,
  scrollThumbs,
  thumbsOffsetStyle,
  imageSizeTrackStyle,
} = useThumbnailControls({ imageContainer, thumbsContainer })

const { data, fetchByFolder } = useCms<SbProductConfig[]>(
  `video-${props.productOrderNumber}`,
)

await fetchLazy(
  fetchByFolder(
    `products/${config.public.appEnv !== 'production' ? 'prev-' : ''}${
      props.productOrderNumber
    }`,
  ),
)

// Storyblok's `starts_with` does prefix matching, so a partial-digit collision
// (e.g. `products/prev-7723` matching another product's `products/prev-77236031`)
// can return a different product's story. Only honor the video when the
// returned story's full_slug actually contains this product's order number.
const videoUrl = computed(() => {
  const story = data.value?.[0]
  if (!story?.content?.video?.filename) {
    return null
  }
  const slug = (story as any)?.full_slug as string | undefined
  if (slug && !slug.includes(String(props.productOrderNumber))) {
    return null
  }
  return story.content.video.filename
})

const items = computed(() => {
  return [
    sortedImages.value[0],
    videoUrl.value && { hash: 'video', videoUrl: videoUrl.value },
    ...sortedImages.value.slice(1),
  ].filter(Boolean)
})

const activeIndex = ref(0)
const isLoaded = ref(false)
const activeItem = computed(() => items.value[activeIndex.value])

watch(
  () => items.value.length,
  (newLength) => {
    if (newLength === 0) {
      activeIndex.value = 0
    } else if (activeIndex.value >= newLength) {
      activeIndex.value = newLength - 1
      isLoaded.value = false
    }
  },
  { immediate: true },
)

const setActiveImage = (index: number) => {
  if (activeIndex.value !== index) {
    activeIndex.value = index
    isLoaded.value = false // Only reset if the image actually changes
    stopHovering()
  }
}

const isHovering = ref(false)
const mousePosition = ref({ x: 0, y: 0 })
const imageContainerRect = ref<DOMRect | null>(null)
let moveFrame = 0
const zoomLevel = 1.8 // Adjust this value to change the zoom level

const handleMouseMove = (event: MouseEvent) => {
  const rect =
    imageContainerRect.value || imageContainer.value?.getBoundingClientRect()
  if (!rect) {
    return
  }
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top
  cancelAnimationFrame(moveFrame)
  moveFrame = requestAnimationFrame(() => {
    mousePosition.value = { x, y }
  })
}

const startHovering = () => {
  imageContainerRect.value =
    imageContainer.value?.getBoundingClientRect() ?? null
  isHovering.value = true
}

const stopHovering = () => {
  cancelAnimationFrame(moveFrame)
  imageContainerRect.value = null
  isHovering.value = false
}

const magnifyingLensStyle = computed(() => {
  const size = 300 // Size of the magnifying lens
  return {
    width: `${size}px`,
    height: `${size}px`,
    left: `${mousePosition.value.x - size / 2}px`,
    top: `${mousePosition.value.y - size / 2}px`,
  }
})

const zoomedImageStyle = computed(() => {
  if (!activeItem.value || !isHovering.value) {
    return {}
  }
  const rect = imageContainerRect.value
  if (!rect) {
    return {}
  }
  const { width, height } = rect
  const lensSize = 300
  const zoomedWidth = width * zoomLevel
  const zoomedHeight = height * zoomLevel
  const x = (mousePosition.value.x / width) * 100
  const y = (mousePosition.value.y / height) * 100

  // Ensure the image is centered within the lens
  const offsetX = lensSize / 2 - mousePosition.value.x * zoomLevel
  const offsetY = lensSize / 2 - mousePosition.value.y * zoomLevel

  return {
    width: `${zoomedWidth}px`,
    height: `${zoomedHeight}px`,
    objectFit: 'cover',
    objectPosition: `${x}% ${y}%`,
    transform: `translate(${offsetX}px, ${offsetY}px)`,
  }
})

const navigateImage = (direction: 'prev' | 'next') => {
  if (direction === 'prev') {
    activeIndex.value =
      (activeIndex.value - 1 + items.value.length) % items.value.length
  } else {
    activeIndex.value = (activeIndex.value + 1) % items.value.length
  }
  isLoaded.value = false
  stopHovering()
}

onBeforeUnmount(() => {
  cancelAnimationFrame(moveFrame)
})

const placeholderSrc = computed(
  () => data.value?.[0]?.content.video_placeholder?.filename,
)
</script>

<style>
.thumbnail-ctrl-icon {
  @apply mx-auto size-6 text-black transition-transform duration-200 group-hover:scale-125;
}
.thumbnail-ctrl-btn {
  @apply pointer-events-auto -ml-2 -mt-1 py-8;
}
</style>
